/* eslint-disable sort-keys */
import React, { useEffect, useState } from "react";

import classNames from "classnames";
import mergeWith from "lodash.mergewith";
import { observer } from "mobx-react";
import dynamic from "next/dynamic";
import Head from "next/head";
import { usePathname, useRouter } from "../shared/hooks/useNavigation";
import { AppProgressBar as NextNProgress } from "next-nprogress-bar";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styled, { ThemeProvider } from "styled-components";

import CookiePolicy from "../components/CookiePolicy/CookiePolicy";
import FacebookPixel from "../components/FacebookPixel/FacebookPixel";
import GlobalCSS from "../components/GlobalCSS/GlobalCSS";
import GoogleAnalytics from "../components/GoogleAnalytics/GoogleAnalytics";
import TopNavigation from "../components/TopNavigation/TopNavigation";
import ApiProvider from "../contexts/ApiContext";
import ApplicationStateProvider from "../contexts/ApplicationStateProvider";
import DataLayerProvider from "../contexts/DataLayerContext";
import { StoreProvider } from "../contexts/StoreContext";
import { setClientCacheEnabled } from "../lib/api";
import Modal from "../shared/components/Modal/Modal";
import Seo from "../shared/components/Seo/Seo";
import useDevice from "../shared/customHooks/useDevice";
import { AnalyticsProvider } from "../shared/helpers/AnalyticsTrackingContext";
import {
  isInIframe,
  useSendHeightToParent,
} from "../shared/helpers/iframeChecker";
import { manageTrackingId, manageSessionId } from "../shared/helpers/tracking";
import { theme, themeCustomizer } from "../shared/styles/theme";
import "../style/index.css";

const LocationRedirectPopup = dynamic(
  () => import("../components/LocationRedirectPopup/LocationRedirectPopup"),
  { ssr: false }
);

function getLogoURL(pageProps, layoutProps) {
  if (
    layoutProps?.singleJobPage?.useComanyLogoInHeader &&
    pageProps?.vacancy?.company?.logoUrlFull
  ) {
    return {
      data: {
        attributes: {
          url: pageProps?.vacancy?.company?.logoUrlFull,
        },
      },
    };
  }

  // v5 format: page fields are directly on the object (flat structure)
  return pageProps?.page?.headerLogo || layoutProps?.singleJobPage?.headerLogo;
}

const StyledPageContainer = styled.div`
  padding-left: ${(props) => (props.$isSideNav ? props.$logoWidth : "0")}px;
`;

function CareersSite(props) {
  const { Component, pageProps } = props;
  const router = useRouter();

  const [fetchedLayoutProps, setFetchedLayoutProps] = useState(null);

  useEffect(() => {
    if (!pageProps?.layoutProps && !fetchedLayoutProps) {
      const locale = router.locale || "en-GB";
      fetch(`/layout-${locale}.json`)
        .then((r) => {
          if (!r.ok) throw new Error("layout file not found");
          return r.json();
        })
        .then(setFetchedLayoutProps)
        .catch(() =>
          fetch(`/api/layout?locale=${locale}`)
            .then((r) => r.json())
            .then(setFetchedLayoutProps)
        );
    }
  }, [pageProps?.layoutProps]);

  const activeLayoutProps = pageProps?.layoutProps || fetchedLayoutProps || {};

  const {
    additionalFooterNavigation,
    additionalHeaderNavigation,
    cacheEnabled,
    countryCodeNavigation,
    applicationFormConfig,
    appConfig,
    appTheme,
    fonts,
    footer,
    footerLinks,
    icons,
    roles,
    topNavigation,
    socialmediaNavigation,
    slugsByPageTypeMap,
    navbarSettings,
    boxLinksMapping,
    popups,
    featureFlags,
  } = activeLayoutProps;

  const [refererSource, setRefererSource] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setRefererSource(params.get("utm_source") || document.referrer || null);
  }, []);

  // Initialize client-side cache value once to avoid runtime API calls to /maintenance
  useEffect(() => {
    if (cacheEnabled !== undefined) {
      setClientCacheEnabled(cacheEnabled);
    }
  }, [cacheEnabled]);

  const { isMobile } = useDevice();
  const isIframe = isInIframe();

  const pathname = usePathname();

  useSendHeightToParent();

  let isMobileOnly = false;
  const isServer = typeof window === "undefined";
  if (!isServer) {
    isMobileOnly = isMobile;
  }

  const mergedTheme = mergeWith({}, theme, appTheme, themeCustomizer);

  const hydrationData = {
    trackingStore: {
      path: pathname,
      refererSource:
        pathname === "/internal-jobs"
          ? "Internal%20Candidate"
          : refererSource,
    },
    rolesStore: {
      roles,
    },
    footerStore: footer,
    filtersStore: {
      searchResults: pageProps?.vacancies,
      totalResults: pageProps?.totalResults,
      boxLinksMapping,
      searchContext: pageProps?.searchContext,
    },
    navigationStore: {
      additionalFooterNavigation,
      additionalHeaderNavigation,
      countryCodeNavigation,
      icons,
      footerLinks,
      topNavigation,
      socialmediaNavigation,
    },
    configStore: { ...appConfig, applicationFormConfig, cacheEnabled },
    homepageStore: pageProps.homepage,
    popupStore: popups,
    ...pageProps?.hydrationData,
  };

  const seoObject = {
    canonicalUrl: pathname,
    metaTitle: appConfig?.siteName,
    ...appConfig?.globalSeo,
  };

  const favIconUrl = appConfig?.favIcon?.url;

  const cssPath =
    process.env.NEXT_PUBLIC_CSS_PATH || appConfig?.additionalCSS?.url;

  const routeId = (pathname || "").replace("/", "");

  const jobPageClass =
    pathname?.includes("/job/") && pageProps?.vacancy?.company?.identifier
      ? pageProps?.vacancy?.company?.identifier
      : "";

  const classes = classNames("d-flex flex-column vh-100", {
    [jobPageClass]: jobPageClass,
  });

  return (
    <StoreProvider hydrationData={hydrationData}>
      <Head>
        {featureFlags?.videoProvider === "vimeo" && (
          <link rel="preconnect" href="https://player.vimeo.com" />
        )}
        {featureFlags?.videoProvider === "mux" && (
          <link rel="preconnect" href="https://stream.mux.com" />
        )}
        {favIconUrl && (
          <link href={favIconUrl} rel="icon" type="image/x-icon" />
        )}
        <link key="css" href={cssPath} rel="stylesheet" />
        {appConfig?.oneTrustID && (
          <>
            <script
              async
              src={`https://cdn-ukwest.onetrust.com/consent/${appConfig?.oneTrustID}/OtAutoBlock.js`}
              type="text/javascript"
            />
            <script
              async
              data-domain-script={appConfig?.oneTrustID}
              src="https://cdn-ukwest.onetrust.com/scripttemplates/otSDKStub.js"
              type="text/javascript"
            />
            <script async id="OptanonWrapper" type="text/javascript">
              function OptanonWrapper() {}
            </script>
          </>
        )}
        <Seo seoAttributes={seoObject} siteName={appConfig?.siteName} />
      </Head>
      <GoogleAnalytics />
      <FacebookPixel />
      <ThemeProvider theme={mergedTheme}>
        <AnalyticsProvider
          env={process.env.ENV}
          userId={manageTrackingId()}
          tenantId={process.env.TENANT_ID}
          sessionId={manageSessionId()}
        >
          {appConfig?.oneTrustID ||
          appConfig?.removeDefaultCookies ||
          isIframe ? null : (
            <CookiePolicy />
          )}
          <ApplicationStateProvider
            asPath={pathname}
            isMobile={isMobileOnly}
            slugsByPageTypeMap={slugsByPageTypeMap}
          >
            <ToastContainer
              position="top-center"
              autoClose={false}
              closeOnClick
              draggable
              pauseOnHover
              style={{ maxWidth: "600px", width: "100%" }}
            />
            <DataLayerProvider>
              <ApiProvider>
                <GlobalCSS
                  backgroundColor={mergedTheme?.bodyBackgroundColor}
                  defaultFont={appConfig?.font?.fontFamily}
                  headerFont={appConfig?.headerFont?.fontFamily}
                  fonts={fonts}
                />
                <NextNProgress color={mergedTheme.secondaryBackgroundColor} />
                <StyledPageContainer
                  className={classes}
                  id={routeId}
                  $isSideNav={
                    navbarSettings?.type === "leftHandSide" && !isIframe
                  }
                  $logoWidth={appConfig?.logoWidth}
                >
                  {isIframe ? null : (
                    <TopNavigation
                      customLogo={getLogoURL(pageProps, activeLayoutProps)}
                      settings={navbarSettings}
                      backgroundColour={pageProps?.page?.headerBackgroundColour}
                    />
                  )}
                  {!isIframe &&
                  appConfig?.showLocationRedirect &&
                  !pathname?.includes("/job/") ? (
                    <LocationRedirectPopup locale={appConfig?.locale} />
                  ) : null}
                  <Component
                    {...pageProps}
                    layoutProps={
                      pageProps?.layoutProps ||
                      (fetchedLayoutProps ? fetchedLayoutProps : undefined)
                    }
                  />
                  {process.env.NEXT_PUBLIC_CSS_PATH && (
                    <h3 className="text-center bg-danger text-black position-sticky bottom-0">
                      LOCAL CSS FILE LOADED!
                    </h3>
                  )}
                  <Modal />
                </StyledPageContainer>
              </ApiProvider>
            </DataLayerProvider>
          </ApplicationStateProvider>
        </AnalyticsProvider>
      </ThemeProvider>
    </StoreProvider>
  );
}

export default observer(CareersSite);
